import React, { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  Alert,
  ActivityIndicator,
  FlatList,
  Linking,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import * as DocumentPicker from 'expo-document-picker';
import * as Contacts from 'expo-contacts';
import { ScreenLayout, Header, Button, colors, spacing, typography } from '../components';
import { useAppContext } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { apiService, DataListWebhookFieldMapping } from '../services/api';
import { BulkContactMapperScreen } from '../components/BulkContactMapperScreen';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface ParsedContact {
  name: string;
  phoneNumber: string;
  isValid: boolean;
  errors?: string[];
}

interface BulkContactUploadScreenProps {
  /** When present, every contact created in this session — device import or
   * CSV/Excel upload — is linked to this System Datalist afterward, and the
   * screen redirects back to that list's records instead of Contacts.
   * Reached from DataListRecordsScreen's "Bulk Add" button for a
   * contacts-type system list. */
  listId?: string;
}

export const BulkContactUploadScreen: React.FC<BulkContactUploadScreenProps> = ({ listId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification } = useAppContext();
  const { currentBusiness, user, bumpContactsRefreshToken } = useAppStore();

  const goBack = () => navigateTo(listId ? 'data-list-records' : 'contacts', listId);

  const linkAndRedirect = async (contactIds: string[]) => {
    if (listId && contactIds.length > 0) {
      try {
        await apiService.linkDataListRecords(listId, contactIds);
      } catch (err) {
        console.error('[BulkContactUploadScreen] Failed to link contacts to data list:', err);
      }
    }
    // ContactsListScreen (and its ContactsList) can still be the same
    // mounted instance after this navigation — its own load only re-runs on
    // an explicit trigger bump, not just because navigateTo pointed back at
    // it. Without this, a genuinely successful import looked like "nothing
    // imported" because the already-rendered list never refetched.
    bumpContactsRefreshToken();
    navigateTo(listId ? 'data-list-records' : 'contacts', listId);
  };
  
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedContacts, setSelectedContacts] = useState<ParsedContact[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadResults, setUploadResults] = useState<any>(null);

  // Mapper flow (CSV/Excel path) — replaces the old "upload straight through
  // a fixed-column template" behavior. previewData holds what
  // previewBulkContactUpload returned (headers/sample rows/suggested
  // mapping); pickedFile is kept because commit re-uploads the SAME file
  // alongside the confirmed mapping rather than persisting it server-side
  // between preview and commit.
  const [showMapper, setShowMapper] = useState(false);
  const [previewData, setPreviewData] = useState<{
    headers: string[];
    sampleRows: Record<string, string>[];
    totalRows: number;
    targetFields: Array<{ id: string; name: string }>;
    suggestedMapping: DataListWebhookFieldMapping[];
    fromRememberedMapping: boolean;
  } | null>(null);
  const [pickedFile, setPickedFile] = useState<{ uri: string; name: string; mimeType: string } | null>(null);
  const [committingMapping, setCommittingMapping] = useState(false);

  /**
   * Request and check contacts permission
   */
  const requestContactsPermission = async (): Promise<boolean> => {
    try {
      // First check existing permission
      const { status: existingStatus } = await Contacts.getPermissionsAsync();
      
      if (existingStatus === 'granted') {
        console.log('✅ [BulkContactUpload] Contacts permission already granted');
        return true;
      }
      
      // Request permission
      console.log('🔍 [BulkContactUpload] Requesting contacts permission...');
      const { status } = await Contacts.requestPermissionsAsync();
      
      if (status === 'granted') {
        console.log('✅ [BulkContactUpload] Contacts permission granted');
        return true;
      }
      
      // Permission denied
      console.log('❌ [BulkContactUpload] Contacts permission denied');
      Alert.alert(
        'Permission Required',
        'DISLIX needs access to your contacts to import them. Please grant permission in your device settings:\n\nSettings > DISLIX > Permissions > Contacts',
        [
          { text: 'Cancel', style: 'cancel' },
          { 
            text: 'Open Settings', 
            onPress: () => {
              if (Platform.OS === 'ios') {
                Linking.openURL('app-settings:');
              } else {
                Linking.openSettings();
              }
            }
          }
        ]
      );
      return false;
    } catch (error) {
      console.error('❌ [BulkContactUpload] Error requesting contacts permission:', error);
      Alert.alert(
        'Permission Error',
        'Failed to request contacts permission. Please try again or check your device settings.',
        [{ text: 'OK' }]
      );
      return false;
    }
  };

  /**
   * Pick contacts from device
   */
  const handlePickDeviceContacts = async () => {
    try {
      // Request permission first
      const hasPermission = await requestContactsPermission();
      if (!hasPermission) return;

      setLoading(true);

      // Get all contacts
      const { data } = await Contacts.getContactsAsync({
        fields: [
          Contacts.Fields.Name,
          Contacts.Fields.PhoneNumbers,
          Contacts.Fields.Emails,
        ],
      });

      if (data.length === 0) {
        showNotification('info', 'No Contacts', 'No contacts found on your device.');
        setLoading(false);
        return;
      }

      // Parse contacts
      const parsedContacts: ParsedContact[] = [];
      
      for (const contact of data) {
        if (!contact.phoneNumbers || contact.phoneNumbers.length === 0) {
          continue; // Skip contacts without phone numbers
        }

        const name = contact.name || 'Unknown';
        const phoneNumber = contact.phoneNumbers[0].number || '';

        if (phoneNumber) {
          parsedContacts.push({
            name,
            phoneNumber: normalizePhoneNumber(phoneNumber),
            isValid: true,
          });
        }
      }

      setSelectedContacts(parsedContacts);
      setShowPreview(true);
      showNotification('success', 'Contacts Loaded', `${parsedContacts.length} contacts loaded from device`);
    } catch (error: any) {
      console.error('Error picking contacts:', error);
      showNotification('error', 'Error', error.message || 'Failed to load device contacts');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Pick CSV/Excel file — parses it generically (previewBulkContactUpload)
   * and shows the mapper for review instead of importing straight through
   * fixed column names. The mapper's own "Import" action calls
   * handleConfirmMapping below, which does the actual upload.
   */
  const handlePickFile = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: [
          'text/csv',
          'text/comma-separated-values',
          'application/vnd.ms-excel',
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ],
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets[0];

      if (!file) {
        showNotification('error', 'Error', 'No file selected');
        return;
      }

      setLoading(true);
      const mimeType = file.mimeType || 'text/csv';
      const response = await apiService.previewBulkContactUpload(file.uri, file.name, mimeType);

      if (response.statusCode === 1000 && response.data) {
        setPickedFile({ uri: file.uri, name: file.name, mimeType });
        setPreviewData(response.data);
        setShowMapper(true);
      } else {
        throw new Error(response.errors?.[0]?.message || 'Failed to read file');
      }
    } catch (error: any) {
      console.error('Error previewing file:', error);
      showNotification('error', 'Upload Failed', error.message || 'Failed to read contacts file');
    } finally {
      setLoading(false);
    }
  };

  /** Polls a bulk-upload job every few seconds until it's completed/failed —
   * the import can take minutes for a few thousand rows, well past what an
   * HTTP request (and the reverse proxy in front of it) will wait for, so
   * the actual work runs server-side as a background job and this just
   * checks in on it periodically instead of holding one long request open. */
  const pollBulkUploadJob = async (jobId: string) => {
    const POLL_INTERVAL_MS = 3000;
    const MAX_ATTEMPTS = 200; // ~10 minutes ceiling

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));

      let statusResponse;
      try {
        statusResponse = await apiService.getBulkContactUploadStatus(jobId);
      } catch (error) {
        // A transient network hiccup while polling shouldn't abandon the
        // job — it's still running server-side regardless. Just retry.
        continue;
      }

      if (statusResponse.statusCode !== 1000 || !statusResponse.data) {
        continue;
      }

      const { status, result, error: jobError, processedCount, processingTotal } = statusResponse.data;
      if (processingTotal) {
        // Real progress — processContacts has started and is reporting how many
        // of the (post-validation/dedup) rows it's actually gotten through.
        setUploadProgress(Math.min(99, Math.round((processedCount / processingTotal) * 100)));
      } else {
        // processingTotal isn't set until validation/dedup finish (fast,
        // in-memory steps) and processContacts actually starts — a small,
        // slowly-climbing placeholder so the bar doesn't look frozen at 0%
        // during that brief window, not a real completion estimate.
        setUploadProgress(Math.min(10, Math.round(((attempt + 1) / 10) * 10)));
      }

      if (status === 'failed') {
        showNotification('error', 'Upload Failed', jobError || 'Failed to import contacts');
        return;
      }

      if (status === 'completed' && result) {
        setUploadResults(result);
        const successful = result.successful || 0;
        const failed = result.failed || 0;

        if (successful === 0) {
          // Every row failed (usually a mapping gap — e.g. no column was
          // routed to any identifier field, so validateContacts rejects
          // every row) — surface the actual reason instead of claiming
          // success, and stay on the mapper so it can be fixed rather than
          // auto-redirecting to an unchanged Contacts list.
          const firstError = result.errors?.[0]?.errors?.[0];
          showNotification(
            'error',
            'Nothing Imported',
            firstError
              ? `${failed} rows failed: ${firstError}. Check your column mapping.`
              : `All ${failed} rows failed. Check your column mapping.`
          );
          return;
        }

        setShowMapper(false);
        showNotification(
          'success',
          'Import Complete',
          `Successfully imported ${successful} contacts.${failed > 0 ? ` ${failed} failed.` : ''}`
        );

        const created: any[] = result.contacts || [];
        const createdIds = created.map((c) => c?._id || c?.id).filter(Boolean);
        setTimeout(() => linkAndRedirect(createdIds), 2000);
        return;
      }
      // status === 'processing' — keep polling
    }

    showNotification(
      'error',
      'Still Processing',
      'This is taking longer than expected. The import is still running in the background — check Contacts again shortly.'
    );
  };

  /** The mapper's confirmed mapping — uploads the file with it, which starts
   * the actual import as a background job, then polls for the outcome. */
  const handleConfirmMapping = async (mapping: DataListWebhookFieldMapping[]) => {
    if (!pickedFile) return;
    setCommittingMapping(true);
    setUploadProgress(0);
    try {
      const response = await apiService.commitBulkContactUpload(
        pickedFile.uri,
        pickedFile.name,
        pickedFile.mimeType,
        mapping,
        'whatsapp'
      );

      if (response.statusCode === 1000 && response.data?.jobId) {
        await pollBulkUploadJob(response.data.jobId);
      } else {
        throw new Error(response.errors?.[0]?.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Error committing bulk upload:', error);
      showNotification('error', 'Upload Failed', error.message || 'Failed to import contacts');
    } finally {
      setCommittingMapping(false);
    }
  };

  /**
   * Import selected device contacts
   */
  const handleImportDeviceContacts = async () => {
    if (selectedContacts.length === 0) {
      showNotification('error', 'No Contacts', 'Please select contacts to import');
      return;
    }

    try {
      setLoading(true);
      setUploadProgress(0);

      // Process contacts in batches
      const batchSize = 50;
      let successCount = 0;
      let failCount = 0;
      const createdIds: string[] = [];

      for (let i = 0; i < selectedContacts.length; i += batchSize) {
        const batch = selectedContacts.slice(i, i + batchSize);

        // Create contacts via API
        for (const contact of batch) {
          try {
            const res = await apiService.addContact({
              name: contact.name,
              phoneNumber: contact.phoneNumber,
              channel: 'whatsapp',
            } as any);
            const createdId = res?.data?.id || res?.data?._id;
            if (createdId) createdIds.push(createdId);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        // Update progress
        setUploadProgress(Math.floor(((i + batch.length) / selectedContacts.length) * 100));
      }

      showNotification(
        'success',
        'Import Complete',
        `Successfully imported ${successCount} contacts. ${failCount} failed.`
      );

      setTimeout(() => linkAndRedirect(createdIds), 2000);
    } catch (error: any) {
      console.error('Error importing contacts:', error);
      showNotification('error', 'Import Failed', error.message || 'Failed to import contacts');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  /**
   * Normalize phone number
   */
  const normalizePhoneNumber = (phone: string): string => {
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    
    // Add + if not present
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    
    return cleaned;
  };

  /**
   * Download sample CSV template
   */
  const handleDownloadTemplate = () => {
    try {
      // Every column the mapper/bulk-upload backend recognizes (see
      // bulk-contact-mapper.util.ts's TARGET_ALIASES) — a filled-in
      // template auto-maps every column on upload instead of needing
      // manual remapping for anything the template itself omitted.
      const csvContent = `name,phone_number,email,whatsapp,facebook-messenger,instagram,telegram,tags,location-address,location-city,location-country,location-url,nationality
Ahmed Ali,+201234567890,ahmed@example.com,+201234567890,,,,customer|vip,,Cairo,Egypt,,Egyptian
Mohamed Hassan,"+201098765432, +201112223334","mohamed@example.com, m.hassan@work.com","+201098765432, +201112223334",,,,customer,,,,,
Fatima Nour,+201555666777,,,,,,new,,,,,
Sarah Ahmed,+201444555666,sarah@example.com,,,,,customer|priority,,,,,
Omar Khalil,+201777888999,,,,,,new,,,,,`;

      if (Platform.OS === 'web') {
        // Web: Create blob and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'sendifier_contacts_template.csv';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        showNotification('success', 'Template Downloaded', 'Check your downloads folder for sendifier_contacts_template.csv');
      } else {
        // Mobile: Show template format in alert
        Alert.alert(
          'Contact Template Format',
          'Create a CSV file with these columns:\n\n' +
          '• name (required)\n' +
          '• phone_number, whatsapp, facebook-messenger, instagram, telegram (at least one required)\n' +
          '• email (optional)\n' +
          '• tags (optional)\n' +
          '• location-address, location-city, location-country (optional)\n' +
          '• location-url — a Google/Apple Maps link, resolved automatically if address/city/country are left blank (optional)\n' +
          '• nationality (optional)\n\n' +
          'Several phones, WhatsApp numbers or emails for one contact? Put them in the same cell separated by commas, e.g. "+201234567890, +201112223334".\n\n' +
          'Example:\nname,phone_number,email,tags\n' +
          'Ahmed Ali,+201234567890,ahmed@example.com,customer|vip\n\n' +
          'Then upload it using "Upload CSV/Excel" option.',
          [{ text: 'Got it' }]
        );
      }
    } catch (error: any) {
      console.error('❌ [BulkContactUpload] Error generating template:', error);
      showNotification('error', 'Error', 'Failed to generate template. Please try again.');
    }
  };

  const renderContactItem = ({ item }: { item: ParsedContact }) => (
    <View style={styles.contactItem}>
      <View style={styles.contactInfo}>
        <Text style={styles.contactName}>{item.name}</Text>
        <Text style={styles.contactPhone}>{item.phoneNumber}</Text>
      </View>
      {item.isValid ? (
        <Ionicons name="checkmark-circle" size={24} color={colors.success} />
      ) : (
        <Ionicons name="alert-circle" size={24} color={colors.error} />
      )}
    </View>
  );

  return (
    <>
    <ScreenLayout
      header={
        <Header
          title="Import Contacts"
          showBack={true}
          onBackPress={goBack}
        />
      }
    >
      <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
        {!showPreview ? (
          <>
            {/* Upload Options */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Import Method</Text>
              
              {/* Device Contacts - Only on mobile */}
              {Platform.OS !== 'web' && (
                <TouchableOpacity
                  style={styles.uploadOption}
                  onPress={handlePickDeviceContacts}
                  disabled={loading}
                >
                  <View style={[styles.optionIcon, { backgroundColor: colors.primary + '20' }]}>
                    <Ionicons name="phone-portrait" size={32} color={colors.primary} />
                  </View>
                  <View style={styles.optionInfo}>
                    <Text style={styles.optionTitle}>Import from Device</Text>
                    <Text style={styles.optionDescription}>
                      Select contacts from your phone's contact list
                    </Text>
                  </View>
                  <DirectionalIcon name="chevron-forward" size={24} color={colors.textLight} />
                </TouchableOpacity>
              )}

              {/* CSV/Excel Upload */}
              <TouchableOpacity
                style={styles.uploadOption}
                onPress={handlePickFile}
                disabled={loading}
              >
                <View style={[styles.optionIcon, { backgroundColor: colors.accent + '20' }]}>
                  <Ionicons name="document-text" size={32} color={colors.accent} />
                </View>
                <View style={styles.optionInfo}>
                  <Text style={styles.optionTitle}>Upload CSV/Excel</Text>
                  <Text style={styles.optionDescription}>
                    Upload a file with your contacts
                  </Text>
                </View>
                <DirectionalIcon name="chevron-forward" size={24} color={colors.textLight} />
              </TouchableOpacity>

              {/* Download Template */}
              <TouchableOpacity
                style={styles.uploadOption}
                onPress={handleDownloadTemplate}
              >
                <View style={[styles.optionIcon, { backgroundColor: colors.success + '20' }]}>
                  <Ionicons name="download" size={32} color={colors.success} />
                </View>
                <View style={styles.optionInfo}>
                  <Text style={styles.optionTitle}>Download Template</Text>
                  <Text style={styles.optionDescription}>
                    Get a sample CSV file to fill in
                  </Text>
                </View>
                <DirectionalIcon name="chevron-forward" size={24} color={colors.textLight} />
              </TouchableOpacity>
            </View>

            {/* Requirements */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Requirements</Text>
              <View style={styles.requirementCard}>
                <View style={styles.requirementItem}>
                  <Ionicons name="checkmark-circle" size={20} color={colors.success} />
                  <Text style={styles.requirementText}>Name (required)</Text>
                </View>
                <View style={styles.requirementItem}>
                  <Ionicons name="checkmark-circle" size={20} color={colors.success} />
                  <Text style={styles.requirementText}>Phone Number (required)</Text>
                </View>
                <View style={styles.requirementItem}>
                  <Ionicons name="information-circle" size={20} color={colors.textLight} />
                  <Text style={styles.requirementText}>Email (optional)</Text>
                </View>
                <View style={styles.requirementItem}>
                  <Ionicons name="information-circle" size={20} color={colors.textLight} />
                  <Text style={styles.requirementText}>Tags (optional)</Text>
                </View>
                <View style={styles.requirementItem}>
                  <Ionicons name="layers-outline" size={20} color={colors.textLight} />
                  <Text style={styles.requirementText}>Several phones, WhatsApp numbers or emails? Separate them with commas in the same cell</Text>
                </View>
              </View>
            </View>

            {/* Loading State */}
            {loading && (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Processing contacts...</Text>
              </View>
            )}
          </>
        ) : (
          <>
            {/* Preview Section */}
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>
                Preview ({selectedContacts.length} contacts)
              </Text>
              
              {uploadProgress > 0 && uploadProgress < 100 && (
                <View style={styles.progressContainer}>
                  <View style={styles.progressBar}>
                    <View style={[styles.progressFill, { width: `${uploadProgress}%` }]} />
                  </View>
                  <Text style={styles.progressText}>{uploadProgress}% Complete</Text>
                </View>
              )}

              <FlatList
                data={selectedContacts.slice(0, 100)} // Show first 100
                renderItem={renderContactItem}
                keyExtractor={(item, index) => `contact-${index}`}
                style={styles.contactsList}
                scrollEnabled={false}
              />
              
              {selectedContacts.length > 100 && (
                <Text style={styles.moreText}>
                  ... and {selectedContacts.length - 100} more contacts
                </Text>
              )}
            </View>

            {/* Action Buttons */}
            <View style={styles.actionButtons}>
              <Button
                title="Cancel"
                onPress={() => {
                  setSelectedContacts([]);
                  setShowPreview(false);
                }}
                variant="outline"
                style={{ flex: 1, marginRight: spacing.sm }}
              />
              <Button
                title={`Import ${selectedContacts.length} Contacts`}
                onPress={handleImportDeviceContacts}
                disabled={loading || selectedContacts.length === 0}
                style={{ flex: 2 }}
              />
            </View>
          </>
        )}

        {/* Upload Results */}
        {uploadResults && (
          <View style={styles.resultsContainer}>
            <Text style={styles.resultsTitle}>Import Results</Text>
            <View style={styles.resultsStats}>
              <View style={styles.statItem}>
                <Ionicons name="checkmark-circle" size={32} color={colors.success} />
                <Text style={styles.statValue}>{uploadResults.totalSuccessful || uploadResults.successful?.length || 0}</Text>
                <Text style={styles.statLabel}>Successful</Text>
              </View>
              <View style={styles.statItem}>
                <Ionicons name="close-circle" size={32} color={colors.error} />
                <Text style={styles.statValue}>{uploadResults.totalFailed || uploadResults.failed?.length || 0}</Text>
                <Text style={styles.statLabel}>Failed</Text>
              </View>
              <View style={styles.statItem}>
                <Ionicons name="warning" size={32} color={colors.warning} />
                <Text style={styles.statValue}>{uploadResults.duplicates || 0}</Text>
                <Text style={styles.statLabel}>Duplicates</Text>
              </View>
            </View>
            
            <Button
              title="Done"
              onPress={goBack}
              style={{ marginTop: spacing.lg }}
            />
          </View>
        )}
      </ScrollView>
    </ScreenLayout>
    {previewData && (
      <BulkContactMapperScreen
        visible={showMapper}
        onClose={() => setShowMapper(false)}
        headers={previewData.headers}
        totalRows={previewData.totalRows}
        targetFields={previewData.targetFields}
        suggestedMapping={previewData.suggestedMapping}
        fromRememberedMapping={previewData.fromRememberedMapping}
        committing={committingMapping}
        onConfirm={handleConfirmMapping}
      />
    )}
    </>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    padding: spacing.lg,
  },
  section: {
    marginBottom: spacing.xl,
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
    marginBottom: spacing.md,
  },
  uploadOption: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.lg,
    marginBottom: spacing.md,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  optionIcon: {
    width: 60,
    height: 60,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  optionInfo: {
    flex: 1,
  },
  optionTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
    marginBottom: 4,
  },
  optionDescription: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.primary,
    color: colors.textLight,
  },
  requirementCard: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.lg,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  requirementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  requirementText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.primary,
    color: colors.textDark,
    marginLeft: spacing.sm,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl,
  },
  loadingText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.primary,
    color: colors.textLight,
    marginTop: spacing.md,
  },
  progressContainer: {
    marginBottom: spacing.lg,
  },
  progressBar: {
    height: 8,
    backgroundColor: colors.border,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: spacing.xs,
  },
  progressFill: {
    height: '100%',
    backgroundColor: colors.primary,
  },
  progressText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.primary,
    color: colors.textLight,
    textAlign: 'center',
  },
  contactsList: {
    maxHeight: 400,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.sm,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  contactInfo: {
    flex: 1,
  },
  contactName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
    marginBottom: 4,
  },
  contactPhone: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.primary,
    color: colors.textLight,
  },
  moreText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.primary,
    color: colors.textLight,
    textAlign: 'center',
    marginTop: spacing.md,
  },
  actionButtons: {
    flexDirection: 'row',
    marginTop: spacing.lg,
    marginBottom: spacing.xl,
  },
  resultsContainer: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.lg,
    padding: spacing.xl,
    marginBottom: spacing.xl,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  resultsTitle: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  resultsStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: spacing.lg,
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontSize: typography.fontSize.xl,
    fontFamily: typography.fontFamily.bold,
    color: colors.textDark,
    marginTop: spacing.xs,
  },
  statLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.primary,
    color: colors.textLight,
    marginTop: spacing.xs,
  },
});

